
// Forced update for subcategory tag v2
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AI_TOOLS } from '../constants';
import { executeToolAPI } from '../services/apiClient';
import ResultViewer from '../components/ResultViewer';
import { PlanType, ToolRun, Tool } from '../types';

interface ToolDetailProps {
  onAddRun: (run: ToolRun) => void;
  credits: number;
  userPlan: PlanType;
}

type UseCaseNumber = 1 | 2 | 3;

const getToolDescription = (tool: Tool) => {
  const description = tool.longDescription || tool.description || '';
  return description.split('<!-- MX_UC_START -->')[0].trim();
};

const prepareAdvancedForm = (html: string | undefined, fallbackDescription: string) => {
  if (!html || typeof DOMParser === 'undefined') {
    return {
      description: fallbackDescription,
      body: '<div class="py-12 text-center"><p class="text-slate-400 italic">No advanced configuration available for this tool.</p></div>',
    };
  }

  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const form = documentFragment.querySelector('form');
  const description = documentFragment.querySelector('.mx-form-header .mx-short')?.textContent?.trim() || fallbackDescription;
  documentFragment.querySelectorAll('.mx-form-header, .mx-submit').forEach((element) => element.remove());

  return {
    description,
    body: form?.innerHTML || documentFragment.body.innerHTML,
  };
};

const useDocumentTheme = () => {
  const readTheme = () => document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const [theme, setTheme] = useState<'light' | 'dark'>(readTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return theme;
};

const UseCaseExampleModal: React.FC<{
  tool: Tool;
  useCase: UseCaseNumber | null;
  onClose: () => void;
}> = ({ tool, useCase, onClose }) => {
  const theme = useDocumentTheme();
  const [inputSample, setInputSample] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const exampleId = tool.catalogId || tool.id;
  const baseUrl = `/examples/${encodeURIComponent(exampleId)}`;
  const screenshotUrl = useCase ? `${baseUrl}/UC${useCase}_${theme}.jpg` : '';
  const htmlUrl = useCase ? `${baseUrl}/UC${useCase}_${theme}.html` : '';

  useEffect(() => {
    if (!useCase) return;

    const controller = new AbortController();
    setIsLoading(true);
    setLoadError('');
    setIsFullscreen(false);
    fetch(`${baseUrl}/input.json`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => setInputSample(JSON.stringify(data, null, 2)))
      .catch((error) => {
        if (error.name !== 'AbortError') setLoadError('The input example could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [baseUrl, useCase]);

  useEffect(() => {
    if (!useCase) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (isFullscreen) setIsFullscreen(false);
      else onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isFullscreen, onClose, useCase]);

  if (!useCase) return null;

  return (
    <>
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="use-case-title">
        <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-900/60">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">Example</p>
              <h2 id="use-case-title" className="text-xl font-black text-slate-900 dark:text-white">{tool.name} · Use Case {useCase}</h2>
            </div>
            <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white" aria-label="Close example">✕</button>
          </div>

          <div className="grid flex-1 gap-6 overflow-y-auto p-6 lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,1.4fr)]">
            <section className="min-w-0">
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Input (JSON)</h3>
              <pre className="max-h-[62vh] overflow-auto whitespace-pre-wrap break-words rounded-2xl border border-slate-200 bg-slate-950 p-5 text-xs leading-relaxed text-slate-100 shadow-inner dark:border-slate-800">
                {isLoading ? 'Loading example…' : loadError || inputSample}
              </pre>
            </section>

            <section className="min-w-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Output ({theme})</h3>
                <button type="button" onClick={() => setIsFullscreen(true)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black uppercase tracking-wider text-slate-600 transition-colors hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-indigo-400 dark:hover:text-indigo-400" title="Open HTML fullscreen">
                  <span aria-hidden="true">⛶</span> Fullscreen
                </button>
              </div>
              <div className="max-h-[62vh] overflow-auto rounded-2xl border border-slate-200 bg-slate-100 p-2 shadow-inner dark:border-slate-800 dark:bg-slate-900">
                <img src={screenshotUrl} alt={`${tool.name} use case ${useCase} ${theme} output`} className="h-auto w-full rounded-xl bg-white" />
              </div>
            </section>
          </div>
        </div>
      </div>

      {isFullscreen && (
        <div className="fixed inset-0 z-[120] flex flex-col bg-white dark:bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-800 dark:bg-slate-900">
            <span className="font-black text-slate-900 dark:text-white">{tool.name} · UC{useCase} · {theme}</span>
            <button type="button" onClick={() => setIsFullscreen(false)} className="rounded-xl bg-slate-900 px-5 py-2 text-xs font-black uppercase tracking-widest text-white dark:bg-white dark:text-slate-900">Close (Esc)</button>
          </div>
          <iframe src={htmlUrl} title={`${tool.name} use case ${useCase} fullscreen example`} className="h-full w-full flex-1 border-0" />
        </div>
      )}
    </>
  );
};

const AdvancedModeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  tool: Tool;
  initialValue: string;
  responseMode: 'short' | 'long' | 'pro';
  onRun: (payload: any) => void;
}> = ({ isOpen, onClose, tool, initialValue, responseMode, onRun }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [detectedInput, setDetectedInput] = useState<string | null>(null);
  const preparedForm = useMemo(
    () => prepareAdvancedForm(tool.form_structure_html, tool.description),
    [tool.description, tool.form_structure_html]
  );

  useEffect(() => {
    if (isOpen && formRef.current) {
      const form = formRef.current;
      const inputs = Array.from(form.querySelectorAll('input, textarea, select')) as (HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[];

      let prefilled = false;
      inputs.forEach(input => {
        const idOrName = (input.id || input.name || "").toLowerCase();
        // Heuristic mapping for diverse CSV field names
        const keywords = ['url', 'website', 'target', 'link', 'query', 'location', 'keyword', 'idea', 'topic', 'context', 'objective', 'brand', 'product'];

        if (keywords.some(k => idOrName.includes(k))) {
          input.value = initialValue;
          prefilled = true;
        }

        if (idOrName === 'response_mode' || idOrName === 'responsemode') {
          input.value = responseMode;
        }
      });

      if (!prefilled && initialValue) {
        setDetectedInput(initialValue);
      } else {
        setDetectedInput(null);
      }
    }
  }, [isOpen, initialValue, responseMode]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const inputs: Record<string, any> = {};
    const fields = Array.from(
      formRef.current.querySelectorAll('input, textarea, select')
    ) as Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

    fields.forEach((field) => {
      const key = field.name || field.id;
      if (!key || field.disabled) {
        return;
      }

      if (field instanceof HTMLInputElement) {
        const type = field.type.toLowerCase();
        if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset') {
          return;
        }
        if (type === 'checkbox') {
          inputs[key] = field.checked;
          return;
        }
        if (type === 'radio') {
          if (field.checked) {
            inputs[key] = field.value;
          }
          return;
        }
      }

      inputs[key] = field.value;
    });

    onRun({
      tool_slug: tool.id,
      mode: "advanced",
      inputs,
      raw_input: initialValue
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-950 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-2xl border border-emerald-500/20 shadow-sm">
              🧩
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                {tool.name}
              </h2>
              <p className="mt-1 max-w-xl text-sm text-slate-500 dark:text-slate-400">{preparedForm.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          {detectedInput && (
            <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-2xl">
              <div className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-1">Unassigned Input Detected</div>
              <p className="text-xs text-indigo-800 dark:text-indigo-200 font-medium italic truncate">"{detectedInput}"</p>
              <p className="text-[9px] text-indigo-600/60 dark:text-indigo-400/60 mt-2 uppercase tracking-tight">Manual field assignment required.</p>
            </div>
          )}

          <div className="mx-advanced-form">
            <form ref={formRef} onSubmit={handleSubmit} id="mx-guided-form">
              <div
                dangerouslySetInnerHTML={{ __html: preparedForm.body }}
              />
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end space-x-3 bg-slate-50 dark:bg-slate-900/50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-xs font-black text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white uppercase tracking-widest transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => formRef.current?.reset()}
            className="px-6 py-3 text-xs font-black text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white uppercase tracking-widest transition-colors"
          >
            Reset
          </button>
          <button
            onClick={() => formRef.current?.requestSubmit()}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black rounded-xl shadow-xl shadow-indigo-600/20 transition-all uppercase tracking-widest"
          >
            🚀 Run Tool
          </button>
        </div>
      </div>
    </div>
  );
};

const ToolDetail: React.FC<ToolDetailProps> = ({ onAddRun, credits, userPlan }) => {
  const { toolId } = useParams<{ toolId: string }>();
  const tool = AI_TOOLS.find(t =>
    t.id === toolId ||
    t.catalogId === toolId ||
    t.commandSlug === toolId
  );
  const [input, setInput] = useState('');
  const [responseMode, setResponseMode] = useState<'short' | 'long' | 'pro'>('short');
  const [isLoading, setIsLoading] = useState(false);
  const [currentRun, setCurrentRun] = useState<ToolRun | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState<UseCaseNumber | null>(null);

  if (!tool) {
    return <div className="p-8 text-center">Tool not found.</div>;
  }

  const handleRun = async (advancedPayload?: any) => {
    if (!advancedPayload && !input.trim()) return;
    const enrichedAdvancedPayload = advancedPayload
      ? {
          ...advancedPayload,
          inputs: {
            ...(advancedPayload.inputs || {}),
            response_mode: advancedPayload.inputs?.response_mode || responseMode,
          },
        }
      : null;
    const finalInput = enrichedAdvancedPayload
      ? JSON.stringify(enrichedAdvancedPayload)
      : JSON.stringify({
          mode: 'quick',
          inputs: {
            query: input,
            keyword: input,
            response_mode: responseMode,
          },
          raw_input: input,
        });
    if (credits < tool.creditCost) {
      alert("Insufficient credits!");
      return;
    }

    setIsLoading(true);
    setCurrentRun(null);
    try {
      // Call backend API instead of direct AI service
      const response = await executeToolAPI(tool.id, finalInput, 'openai');

      if (!response.success) {
        throw new Error(response.error || 'Tool execution failed');
      }

      const rawEnvelope = (response.data && typeof response.data === 'object')
        ? response.data
        : {
            raw: String(response.data ?? ''),
            render_blocks: [],
          };

      const requestContext = enrichedAdvancedPayload?.inputs && typeof enrichedAdvancedPayload.inputs === 'object'
        ? enrichedAdvancedPayload.inputs
        : (() => {
            try {
              const parsed = JSON.parse(finalInput);
              return parsed?.inputs && typeof parsed.inputs === 'object' ? parsed.inputs : null;
            } catch {
              return null;
            }
          })();
      const requestedResponseMode = requestContext?.response_mode || requestContext?.responseMode || rawEnvelope.meta?.response_mode;

      const canonicalEnvelope = {
        ...rawEnvelope,
        meta: {
          ...(rawEnvelope.meta || {}),
          tool_id: tool.id,
          user_plan: userPlan,
          response_mode: requestedResponseMode || responseMode || rawEnvelope.meta?.response_mode,
          result_engine: tool.resultEngine || rawEnvelope.meta?.result_engine,
          result_template_id: tool.resultTemplateId || rawEnvelope.meta?.result_template_id,
          request_context: requestContext || rawEnvelope.meta?.request_context,
        },
        render_map: rawEnvelope.render_map || rawEnvelope.result_render_map_json || tool.resultRenderMap,
      };

      // Transform backend response to match ToolRun format
      const newRun: ToolRun = {
        id: canonicalEnvelope.run_id || `run_${Date.now()}`,
        toolId: tool.id,
        timestamp: Date.now(),
        creditsSpent: tool.creditCost,
        status: 'Completed',
        summaryText: canonicalEnvelope.raw || JSON.stringify(canonicalEnvelope, null, 2),
        htmlContent: `<pre>${canonicalEnvelope.raw || JSON.stringify(canonicalEnvelope, null, 2)}</pre>`,
        normalizedOutput: canonicalEnvelope.raw || canonicalEnvelope,
        renderBlocks: canonicalEnvelope.render_blocks,
        responseEnvelope: canonicalEnvelope as any,
      };
      setCurrentRun(newRun);
      onAddRun(newRun);
    } catch (err) {
      console.error(err);
      alert("Error executing tool. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex flex-col space-y-2">
        <Link to="/app/tools" className="inline-flex items-center text-sm font-semibold text-mx-text-secondary hover:text-mx-accent transition-colors">
          <span className="mr-2">←</span> Back to Library
        </Link>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-mx-bg-primary p-8 shadow-sm transition-colors dark:border-slate-800">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-slate-100 bg-mx-bg-secondary text-4xl shadow-inner dark:border-slate-800">
            {tool.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-black leading-tight tracking-tight text-mx-text-primary">{tool.name}</h1>
                  <span className="category-pill">{tool.subCategory}</span>
                  {tool.badges.includes('Free') && (
                    <span className="rounded border border-emerald-500/20 bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-500">Free Tool</span>
                  )}
                </div>
                <p className="max-w-3xl whitespace-pre-line text-sm leading-relaxed text-mx-text-secondary">{getToolDescription(tool)}</p>
              </div>
              <div className="shrink-0 rounded-2xl border border-slate-200 bg-mx-bg-secondary px-5 py-4 text-center shadow-inner dark:border-slate-800">
                <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-mx-text-muted">Execution cost</div>
                <div className="text-2xl font-black text-mx-accent">{tool.creditCost === 0 ? 'FREE' : tool.creditCost} <span className="text-xs">{tool.creditCost === 0 ? '' : 'CREDITS'}</span></div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-5 dark:border-slate-800">
              <span className="mr-1 text-xs font-black uppercase tracking-widest text-mx-text-muted">Use cases:</span>
              {([1, 2, 3] as UseCaseNumber[]).map((useCase) => (
                <button key={useCase} type="button" onClick={() => setSelectedUseCase(useCase)} className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-mx-accent bg-mx-bg-primary text-sm font-black text-mx-accent transition-all hover:bg-mx-accent hover:text-white focus:outline-none focus:ring-4 focus:ring-mx-accent/20" aria-label={`Open use case ${useCase}`}>
                  {useCase}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-mx-bg-primary border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm">
            <h3 className="font-bold text-mx-text-primary mb-4">Configuration</h3>
            <div className="space-y-4">
              <div className="relative group">
                <label className="block text-[10px] font-black text-mx-text-muted uppercase tracking-widest mb-2">ShadowEngine Prompt</label>
                <div className="relative">
                  <textarea
                    className="w-full bg-mx-bg-secondary border border-slate-200 dark:border-slate-800 rounded-2xl p-4 pb-14 text-sm text-mx-text-primary focus:outline-none focus:ring-2 focus:ring-mx-accent/50 min-h-[220px] shadow-inner resize-none transition-all"
                    placeholder="Enter URL, topic, or keyword"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  ></textarea>

                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="absolute right-3 bottom-3 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm flex items-center space-x-2 hover:border-indigo-500 hover:text-indigo-600 transition-all active:scale-95 group/btn"
                    title="Guided Form"
                  >
                    <span className="text-[10px] font-black uppercase tracking-tighter">Advanced <span className="hidden sm:inline">Mode</span></span>
                    <span className="text-sm group-hover/btn:rotate-45 transition-transform duration-300">⚙️</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-mx-text-muted uppercase tracking-widest mb-2">Result Mode</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'short', label: 'Short' },
                    { value: 'long', label: 'Long' },
                    { value: 'pro', label: 'Pro' },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setResponseMode(mode.value as 'short' | 'long' | 'pro')}
                      className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                        responseMode === mode.value
                          ? 'bg-mx-accent text-white border-mx-accent shadow-lg shadow-mx-accent/20'
                          : 'bg-mx-bg-secondary text-mx-text-secondary border-slate-200 dark:border-slate-800 hover:border-mx-accent/50'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleRun()}
                disabled={isLoading}
                className="w-full py-4 bg-mx-accent hover:opacity-90 disabled:bg-slate-300 dark:disabled:bg-slate-800 text-white font-black rounded-2xl shadow-xl shadow-mx-accent/30 transition-all flex items-center justify-center space-x-2 tracking-widest uppercase"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>🚀 Run Tool</span>
                )}
              </button>
            </div>
          </div>

          <div className="p-6 bg-mx-accent-soft/30 border border-mx-accent/20 rounded-3xl">
            <h4 className="text-mx-accent font-bold text-sm mb-2 flex items-center tracking-tight">
              <span className="mr-2">💡</span> Pro Tip
            </h4>
            <p className="text-xs text-mx-text-secondary leading-relaxed">
              Use <b>Advanced Mode</b> to bypass generic extraction and target specific campaign parameters directly.
            </p>
          </div>
        </div>

        <div className="lg:col-span-2">
          {currentRun ? (
            <ResultViewer run={currentRun} tool={tool} userPlan={userPlan} onRunAgain={() => handleRun()} />
          ) : (
            <div className="h-full min-h-[500px] bg-mx-bg-secondary/50 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl flex flex-col items-center justify-center text-center p-12 transition-colors">
              {isLoading ? (
                <div className="space-y-6 flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full border-4 border-mx-accent border-t-transparent animate-spin shadow-lg"></div>
                  <div className="space-y-1">
                    <p className="text-mx-text-primary font-bold text-lg">ShadowEngine Processing</p>
                    <p className="text-xs text-mx-text-muted uppercase tracking-widest">Optimizing high-fidelity output...</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-6xl opacity-20 text-mx-text-muted">⚡</div>
                  <h3 className="text-xl font-bold text-mx-text-muted uppercase tracking-wider">Engine Ready</h3>
                  <p className="text-sm text-mx-text-muted max-w-sm">Detailed HTML analysis will be displayed in this panel after the run is complete.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AdvancedModeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tool={tool}
        initialValue={input}
        responseMode={responseMode}
        onRun={(payload) => handleRun(payload)}
      />

      <UseCaseExampleModal
        tool={tool}
        useCase={selectedUseCase}
        onClose={() => setSelectedUseCase(null)}
      />

      <style>{`
        .category-pill {
          background: var(--accent-soft);
          color: var(--accent);
          font-size: 11px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          letter-spacing: -0.01em;
          white-space: nowrap;
        }
        .status-badge {
          background: rgba(100, 116, 139, 0.1);
          color: var(--text-secondary);
          font-size: 9px;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          white-space: nowrap;
        }
        .dark .status-badge {
          background: rgba(255, 255, 255, 0.05);
          color: var(--text-muted);
        }
        
        /* Scoped Advanced Mode Form Styles for CSV HTML */
        .mx-advanced-form { color: var(--text-primary); }
        .mx-advanced-form .mx-field { margin-bottom: 1.5rem; }
        .mx-advanced-form .mx-label {
          display: block; font-size: 10px; font-weight: 900; text-transform: uppercase;
          letter-spacing: 0.05em; color: var(--text-muted); margin-bottom: 0.5rem;
        }
        .mx-advanced-form .mx-req { color: #ef4444; margin-left: 2px; }
        
        .mx-advanced-form input, .mx-advanced-form textarea, .mx-advanced-form select {
          width: 100%; background: var(--bg-secondary); border: 1px solid rgba(0,0,0,0.1);
          border-radius: 0.75rem; padding: 0.75rem; font-size: 0.875rem; transition: all 0.2s;
        }
        
        .dark .mx-advanced-form input, .dark .mx-advanced-form textarea, .dark .mx-advanced-form select {
          border-color: rgba(255,255,255,0.1);
          background: #111827;
          color: #e5e7eb;
        }
        
        .mx-advanced-form input:focus, .mx-advanced-form textarea:focus, .mx-advanced-form select:focus {
          outline: none; box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2); border-color: var(--accent);
        }
        
        .mx-advanced-form details { margin-top: 1rem; font-size: 0.875rem; border-top: 1px solid rgba(0,0,0,0.05); padding-top: 0.75rem; }
        .mx-advanced-form summary { cursor: pointer; font-weight: bold; color: var(--accent); outline: none; }
        .mx-advanced-form .mx-submit { display: none; }
        
        .mx-advanced-form .mx-metrics { display: flex; gap: 1rem; margin-bottom: 1.5rem; }
        .mx-advanced-form .mx-metric { background: rgba(0,0,0,0.05); padding: 0.5rem 1rem; border-radius: 0.5rem; }
        .mx-advanced-form .mx-metric-label { font-size: 9px; font-weight: bold; text-transform: uppercase; color: var(--text-muted); display: block; }
        .mx-advanced-form .mx-metric-value { font-size: 14px; font-weight: 900; color: var(--accent); }
      `}</style>
    </div>
  );
};

export default ToolDetail;
